import React from 'react';
import { useParams } from 'react-router-dom';

import { EProductCosts } from '@domain/enums/common/EProductCosts';
import { EAlertVariant } from '@domain/enums/components/EAlert';
import { EHeadingSize, EHeadingWeight } from '@domain/enums/components/EHeading';
import { ESynchronizationType } from '@domain/enums/hooks/ESynchronization';
import { IParams } from '@domain/interfaces/IParams';

import { useSynchronization } from '@helpers/hooks/common/useSynchronization';
import { useDate } from '@helpers/hooks/useDate';

import { getSynchronizationStatus } from '@helpers/utils/common/synchronization';

import { ERPS } from '@constants/common/integrations';

import { useStoreIntegrations } from '@helpers/hooks/pages/dashboard/integrationCenter/useStoreIntegrations';
import { useToast } from '@helpers/hooks/useToast';
import * as S from './styles';

const SyncOption: React.FC = () => {
  const { toast } = useToast();
  const { handleSideModalOpen } = useStoreIntegrations();
  const foundIntegration = ERPS.find(
    integration => integration.identifier === EProductCosts.DSERS,
  ) as typeof ERPS[number];

  const { utcToZonedTime, format } = useDate();
  const { storeAliasId } = useParams<IParams>();
  const {
    addSynchronization,
    currentSynchronizations,
    synchronizationsQueue,
  } = useSynchronization();

  const [period, setPeriod] = React.useState({
    startDate: utcToZonedTime(new Date()),
    endDate: utcToZonedTime(new Date()),
  });

  const syncOrdersRef = React.useRef<HTMLInputElement>(null);
  const syncProductsRef = React.useRef<HTMLInputElement>(null);

  const onSync = React.useCallback(async () => {
    const { startDate, endDate } = period;

    if (syncOrdersRef.current && syncProductsRef.current) {
      if (syncProductsRef.current.checked) {
        const params = `?start_date=${format(new Date(2000, 1, 1), 'yyyy-MM-dd')}&end_date=${format(
          utcToZonedTime(endDate),
          'yyyy-MM-dd',
        )}`;

        addSynchronization({
          name: foundIntegration.name,
          type: ESynchronizationType.STORE_PRODUCT,
          label: 'Produtos',
          dependencies: [],
          storeAliasId,
          config: {
            isOnboardingSynchronization: false,
            showNotification: true,
          },
          request: foundIntegration.generateSyncRequest(
            ESynchronizationType.STORE_PRODUCT,
            storeAliasId,
            params,
          ),
        });
      }

      if (syncOrdersRef.current.checked) {
        const params = `?start_date=${format(
          utcToZonedTime(startDate),
          'yyyy-MM-dd',
        )}&end_date=${format(utcToZonedTime(endDate), 'yyyy-MM-dd')}`;

        addSynchronization({
          name: foundIntegration.name,
          type: ESynchronizationType.STORE_ORDER,
          label: 'Pedidos',
          dependencies: [ESynchronizationType.STORE_PRODUCT],
          storeAliasId,
          config: {
            isOnboardingSynchronization: false,
            showNotification: true,
          },
          request: foundIntegration.generateSyncRequest(
            ESynchronizationType.STORE_ORDER,
            storeAliasId,
            params,
          ),
        });
      }

      toast.success('A sincronização iniciou!');
      handleSideModalOpen();
    }
  }, [
    period,
    toast,
    handleSideModalOpen,
    format,
    utcToZonedTime,
    addSynchronization,
    foundIntegration,
    storeAliasId,
  ]);

  const isSyncingProducts = getSynchronizationStatus(
    currentSynchronizations,
    synchronizationsQueue,
    foundIntegration.name,
    ESynchronizationType.STORE_PRODUCT,
    storeAliasId,
  );
  const isSyncingOrders = getSynchronizationStatus(
    currentSynchronizations,
    synchronizationsQueue,
    foundIntegration.name,
    ESynchronizationType.STORE_ORDER,
    storeAliasId,
  );

  const handleOrdersChange = React.useCallback(e => {
    if (e.target.checked) {
      const isProductDisabled = syncProductsRef?.current?.checked === false;

      if (isProductDisabled) {
        syncProductsRef.current.checked = true;
      }
    }
  }, []);

  return (
    <S.Content>
      <S.InputGroup>
        <S.Title type={EHeadingSize.H5} fontWeight={EHeadingWeight.REGULAR}>
          Escolha o que sincronizar
        </S.Title>

        <S.SwitchGroup>
          <S.Switch
            id="sync_products"
            name="sync_products"
            isLoading={false}
            defaultChecked
            innerRef={syncProductsRef}
          />

          <S.Label>Produtos</S.Label>
        </S.SwitchGroup>

        <S.SwitchGroup>
          <S.Switch
            id="sync_orders"
            name="sync_orders"
            isLoading={false}
            defaultChecked
            innerRef={syncOrdersRef}
            onChange={handleOrdersChange}
          />

          <S.Label>Pedidos</S.Label>
        </S.SwitchGroup>
      </S.InputGroup>

      <S.Title type={EHeadingSize.H5} fontWeight={EHeadingWeight.REGULAR}>
        Selecione um período para sincronizar
      </S.Title>

      <S.Alert variant={EAlertVariant.WARNING} outlineIcon>
        O período para selecionar poderá ser no máximo 30 dias.
      </S.Alert>

      <S.CustomDatePicker
        period={period}
        setPeriod={setPeriod}
        maxDate={utcToZonedTime(new Date())}
        maxRange={30}
      />

      <S.SyncButton
        onClick={onSync}
        isLoading={isSyncingProducts || isSyncingOrders}
        disabled={isSyncingProducts || isSyncingOrders}
      >
        Sincronizar
      </S.SyncButton>
    </S.Content>
  );
};

export default SyncOption;
